import React, { useState, useRef, useEffect } from "react";
import {
  FiFile,
  FiPlus,
  FiDownload,
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiList,
  FiLoader,
  FiSave,
  FiUpload,
} from "react-icons/fi";
import { MdFormatListNumbered, MdFormatColorText } from "react-icons/md";
import "./styles/QnrDesign.css";
import config from "../configs/config";
import { useToast } from "../contexts/ToastContext";

export default function QnrDesign() {
  const { toast } = useToast();
  const [output, setOutput] = useState("");

  const [isExporting, setIsExporting] = useState(false);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);

  // Sidebar states
  const [savedQuestionnaires, setSavedQuestionnaires] = useState([]);
  const [showNameInput, setShowNameInput] = useState(false);
  const [newQnrName, setNewQnrName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessingSidebar, setIsProcessingSidebar] = useState(false);

  const sidebarFileInputRef = useRef(null);
  const outputRef = useRef(null);

  // A questionnaire is loaded once something has been selected from the
  // sidebar; drives whether the editor canvas or the empty state renders.
  const hasGenerated = !!currentQuestionnaire;

  useEffect(() => {
    if (outputRef.current && output && hasGenerated) {
      outputRef.current.innerHTML = output;
    }
  }, [output, hasGenerated]);

  useEffect(() => {
    fetchSavedQuestionnaires();
  }, []); // Empty dependency array means it runs once on component mount

  // Add this function to fetch questionnaires from the database
  // 1. Fix fetchSavedQuestionnaires - Add better error handling and logging
  const fetchSavedQuestionnaires = async () => {
    try {
      setIsProcessingSidebar(true);

      console.log(
        "🔍 Fetching questionnaires from:",
        `${config.API_BASE_URL}/get-questionnaires`
      );

      const response = await fetch(
        `${config.API_BASE_URL}/get-questionnaires`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      console.log("📡 Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error("Failed to fetch questionnaires");
      }

      const data = await response.json();
      console.log("📦 Received data:", data);

      if (data.success && data.questionnaires) {
        // Transform the data to match your frontend structure
        const transformedQuestionnaires = data.questionnaires.map((qnr) => {
          // Parse sqgroup_json if it's a string
          let jsonData = qnr.sqgroup_json;
          if (typeof jsonData === "string") {
            try {
              jsonData = JSON.parse(jsonData);
            } catch (e) {
              console.error(`Failed to parse JSON for ${qnr.filename}:`, e);
              jsonData = [];
            }
          }

          return {
            id: qnr.id,
            name: qnr.filename.replace(/\.(docx|doc)$/i, ""), // Remove extension
            json: jsonData,
            totalQuestions: Array.isArray(jsonData) ? jsonData.length : 0,
            createdAt: qnr.created_at || new Date().toISOString(),
          };
        });

        console.log(
          "✅ Transformed questionnaires:",
          transformedQuestionnaires
        );
        setSavedQuestionnaires(transformedQuestionnaires);
        console.log(
          `✅ Loaded ${transformedQuestionnaires.length} questionnaires`
        );
      } else {
        console.warn("⚠️ No questionnaires in response or success=false");
        setSavedQuestionnaires([]);
      }
    } catch (error) {
      console.error("❌ Error fetching questionnaires:", error);
      // Show error but don't alert on mount
      setSavedQuestionnaires([]);
    } finally {
      setIsProcessingSidebar(false);
    }
  };

  // 2. Fix handleSaveQuestionnaire


  const convertJsonToHtml = (jsonData) => {
    let html = "";

    jsonData.forEach((question, index) => {
      // Question number and title
      html += `<h2 style="font-size: 16px;">${question.Question_number || `Q${index + 1}`
        }</h2>`;
      html += `<p style="font-size: 12px;"><strong>${question.Qtitle}</strong></p>`;

      // Instructions
      if (question.instructions && question.instructions.length > 0) {
        question.instructions.forEach((instruction) => {
          html += `<p style="font-size: 11px;">${instruction}</p>`;
        });
      }

      // Handle different question types
      if (
        question.type === "Numeric Grid" ||
        (question.rows &&
          question.rows.length > 0 &&
          question.columns &&
          question.columns.length > 0)
      ) {
        // Grid question with table
        html +=
          '<table style="border-collapse: collapse; width: 100%; margin: 2px 0; font-size: 10px; table-layout: fixed;">';

        // Table header
        html += "<thead><tr>";
        html +=
          '<th style="border: 1px solid #333; padding: 6px; background-color: #e0e0e0; font-size: 10px; word-wrap: break-word; white-space: normal; width: 20%;"></th>';
        question.columns.forEach((col) => {
          html += `<th style="border: 1px solid #333; padding: 6px; background-color: #e0e0e0; font-size: 10px; word-wrap: break-word; white-space: normal;">${col.text}</th>`;
        });
        html += "</tr></thead>";

        // Table body
        html += "<tbody>";
        question.rows.forEach((row) => {
          html += "<tr>";
          html += `<td style="border: 1px solid #333; padding: 6px; font-size: 10px; word-wrap: break-word; white-space: normal;"><strong>${row.text}</strong></td>`;
          question.columns.forEach(() => {
            html +=
              '<td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 10px; word-wrap: break-word; white-space: normal;"></td>';
          });
          html += "</tr>";
        });
        html += "</tbody></table>";
      } else if (question.rows && question.rows.length > 0) {
        // Single/Multiple Response with rows only
        html += '<ul style="font-size: 11px;">';
        question.rows.forEach((row) => {
          const symbol = question.type === "Single Response" ? "○" : "☐";
          html += `<li>${symbol} ${row.text}</li>`;
        });
        html += "</ul>";
      } else if (question.type === "Numeric") {
        // Numeric input
        html +=
          '<p style="margin: 10px 0; font-size: 11px;">__________ (Numeric response)</p>';
      }

      // Add spacing between questions
      html += "<br><br>";
    });

    return html;
  };

  const convertHtmlToJson = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const questions = [];
    let currentQuestion = null;

    const allElements = Array.from(doc.body.children);

    allElements.forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      const textContent = element.textContent.trim();

      // Detect question headers (h2, h3)
      if (tagName === "h2" || tagName === "h3") {
        if (currentQuestion) {
          questions.push(currentQuestion);
        }

        currentQuestion = {
          Question_number: textContent,
          Qtitle: "",
          instructions: [],
          type: "Single Response",
          rows: [],
          columns: [],
          statements: [],
          logics: "",
          _order: questions.length + 1,
        };
      }
      // Detect question title (bold text in paragraph)
      else if (
        currentQuestion &&
        tagName === "p" &&
        element.querySelector("strong")
      ) {
        const strongText = element.querySelector("strong").textContent;
        currentQuestion.Qtitle = strongText;
      }
      // Detect instructions
      else if (
        currentQuestion &&
        tagName === "p" &&
        !element.querySelector("strong")
      ) {
        if (textContent && !textContent.includes("__________")) {
          currentQuestion.instructions.push(textContent);
        }
      }
      // Detect tables (grids)
      else if (currentQuestion && tagName === "table") {
        const rows = Array.from(element.querySelectorAll("tbody tr"));
        const headers = Array.from(element.querySelectorAll("thead th"));

        const columns = headers.slice(1).map((th, idx) => ({
          index: `c${idx + 1}`,
          text: th.textContent.trim(),
        }));

        const rowData = rows.map((tr, idx) => {
          const cells = Array.from(tr.querySelectorAll("td"));
          return {
            index: `r${idx + 1}`,
            text: cells[0]?.textContent.trim() || "",
          };
        });

        currentQuestion.rows = rowData;
        currentQuestion.columns = columns;
        currentQuestion.type =
          columns.length > 1 ? "SingleGrid" : "Single Response";
      }
      // Detect lists (single/multiple choice)
      else if (currentQuestion && tagName === "ul") {
        const listItems = Array.from(element.querySelectorAll("li"));
        const rows = listItems.map((li, idx) => {
          let text = li.textContent.trim();
          // Remove radio/checkbox symbols
          text = text.replace(/^[○☐✓]\s*/, "");

          return {
            index: `r${idx + 1}`,
            text: text,
          };
        });

        currentQuestion.rows = rows;
        const hasCheckbox = listItems.some((li) =>
          li.textContent.includes("☐")
        );
        currentQuestion.type = hasCheckbox
          ? "Multiple Response"
          : "Single Response";
      }
      // Detect numeric input
      else if (currentQuestion && textContent.includes("__________")) {
        currentQuestion.type = "Numeric";
        currentQuestion.oeParameters = { min: "", max: "", isHidden: false };
      }
    });

    if (currentQuestion) {
      questions.push(currentQuestion);
    }

    return questions; // Return as array
  };

  const exportToSurveyBuilder = async () => {
    const htmlContent = outputRef.current?.innerHTML || output;

    if (!htmlContent.trim()) {
      toast.warning("No content to export");
      return;
    }

    setIsExporting(true);

    try {
      const jsonArray = convertHtmlToJson(htmlContent);

      if (jsonArray.length === 0) {
        throw new Error("No questions found in the document");
      }

      console.log("📤 Converted to JSON array:", jsonArray);

      // ✅ Use the current questionnaire's filename or create a temp one
      const filename = currentQuestionnaire?.name || "temp_unsaved_qnr";
      const questionnaireId = currentQuestionnaire?.id || null;

      // Send to backend for storage
      const response = await fetch(`${config.API_BASE_URL}/store-qnr-export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          filename: filename,
          questionnaireId: questionnaireId,
          data: jsonArray,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to store export data");
      }

      const result = await response.json();
      console.log("✅ Export stored:", result);

      // Store the export identifier for Survey Builder
      localStorage.setItem("qnr_export_filename", filename);
      localStorage.setItem("qnr_export_id", result.questionnaireId || filename);

      // Also try window object as fallback for small datasets
      if (jsonArray.length <= 10) {
        window.__QNR_EXPORT__ = jsonArray;
      }

      toast.success(`Successfully updated "${filename}"! ${jsonArray.length} questions prepared for Survey Builder.`);
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Sidebar functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Extract filename without extension
      const fileNameWithoutExt = file.name.replace(/\.(docx|doc)$/i, "");
      setSelectedFile(file);
      setNewQnrName(fileNameWithoutExt);
      setShowNameInput(true);
    }
  };

  const handleSaveQuestionnaire = async () => {
    if (!selectedFile) {
      toast.warning("Please select a DOCX file first");
      return;
    }

    if (!newQnrName.trim()) {
      toast.warning("Please enter a name for this questionnaire");
      return;
    }

    setIsProcessingSidebar(true);

    try {
      const formData = new FormData();
      formData.append("questionnaire", selectedFile);
      formData.append("model", "gemini");
      formData.append("questionnaire_name", newQnrName);

      const response = await fetch(
        `${config.API_BASE_URL}/process-questionnaire-to-json`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to process questionnaire");
      }

      const data = await response.json();
      await fetchSavedQuestionnaires();

      toast.success(`Successfully saved! ${data.total_questions || data.sqgroup?.length || 0} questions extracted.`);

      setSelectedFile(null);
      setNewQnrName("");
      setShowNameInput(false);
      if (sidebarFileInputRef.current) {
        sidebarFileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsProcessingSidebar(false);
    }
  };

  const cancelNameInput = () => {
    setShowNameInput(false);
    setNewQnrName("");
    setSelectedFile(null);
    if (sidebarFileInputRef.current) {
      sidebarFileInputRef.current.value = "";
    }
  };

  const loadQuestionnaire = (qnr) => {
    const htmlContent = convertJsonToHtml(qnr.json);
    setOutput(htmlContent);
    setCurrentQuestionnaire(qnr);
  };

  const exportToWord = () => {
    const contentToExport = outputRef.current
      ? outputRef.current.innerHTML
      : output;
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Questionnaire</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
          table { border-collapse: collapse; width: 100%; margin: 10px 0; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>${contentToExport}</body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "questionnaire.doc";
    a.click();
    URL.revokeObjectURL(url);
  };

  const startNewQuestionnaire = () => {
    setOutput("");
    setCurrentQuestionnaire(null);
  };

  const applyFormat = (command, value = null) => {
    if (outputRef.current) {
      outputRef.current.focus();
      document.execCommand(command, false, value);
    }
  };

  return (
    <div className={`qnr-main-layout ${hasGenerated ? "has-generated" : ""}`}>
      {/* Permanent Sidebar */}
      <div className="sidebar-permanent">
        <div className="sidebar-header">
          <h2>Questionnaires</h2>
        </div>

        <div className="sidebar-content">
          {/* New Questionnaire Button */}
          {!showNameInput && (
            <>
              <input
                ref={sidebarFileInputRef}
                type="file"
                accept=".docx,.doc"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <button
                onClick={() => sidebarFileInputRef.current?.click()}
                className="new-qnr-btn"
                disabled={isProcessingSidebar}
              >
                <FiPlus size={20} />
              </button>
            </>
          )}

          {/* Name Input Form */}
          {showNameInput && (
            <div className="name-input-section">
              <label className="sidebar-label">Questionnaire Name</label>
              <input
                type="text"
                value={newQnrName}
                onChange={(e) => setNewQnrName(e.target.value)}
                placeholder="Enter name..."
                className="sidebar-input"
                disabled={isProcessingSidebar}
                autoFocus
              />
              <div className="name-input-actions">
                <button
                  onClick={handleSaveQuestionnaire}
                  disabled={isProcessingSidebar || !newQnrName.trim()}
                  className="save-btn"
                >
                  {isProcessingSidebar ? (
                    <>
                      <FiLoader className="spinner" size={14} />
                    </>
                  ) : (
                    <>
                      <FiSave size={14} />
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={cancelNameInput}
                  disabled={isProcessingSidebar}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Saved Questionnaires List */}
          {savedQuestionnaires.length > 0 && (
            <div className="qnr-list">
              <div className="qnr-list-header">Saved Questionnaires</div>
              {savedQuestionnaires.map((qnr) => (
                <div
                  key={qnr.id}
                  className="qnr-item"
                  onClick={() => loadQuestionnaire(qnr)}
                >
                  <div className="qnr-item-icon">
                    <FiFile size={16} />
                  </div>
                  <div className="qnr-item-content">
                    <div className="qnr-item-name">{qnr.name}</div>
                    <div className="qnr-item-meta">
                      {qnr.totalQuestions} questions
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {savedQuestionnaires.length === 0 && !showNameInput && (
            <div className="empty-state">
              <FiFile size={48} color="#d1d5db" />
              <p>No questionnaires yet</p>
              <p className="empty-state-hint">
                Click the + button to create one
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="qnr-main-content">
        <div className="dev-banner">
          <div className="dev-banner-content">
            <span className="dev-banner-icon">🚧</span>
            <span className="dev-banner-text">
              <strong>Qnr Design</strong> is currently in active development.
              Features are being added and improved regularly.
            </span>
            <span className="dev-banner-version">v0.1.0-beta</span>
          </div>
        </div>

        <div className={`qnr-container ${hasGenerated ? "has-generated" : ""}`}>
          {!hasGenerated && (
            <div className="empty-state">
              <FiFile size={48} color="#d1d5db" />
              <p>Select a questionnaire from the sidebar to start editing</p>
              <p className="empty-state-hint">
                Or click the + button to upload a new one
              </p>
            </div>
          )}

          {hasGenerated && (
            <div className="split-container centered-view">
              <div className="output-section centered">
                {/* Formatting Toolbar */}
                <div className="formatting-toolbar">
                  <div className="toolbar-left">
                    <button
                      onClick={() => applyFormat("bold")}
                      className="format-btn"
                      title="Bold"
                    >
                      <FiBold size={12} />
                    </button>
                    <button
                      onClick={() => applyFormat("italic")}
                      className="format-btn"
                      title="Italic"
                    >
                      <FiItalic size={12} />
                    </button>
                    <button
                      onClick={() => applyFormat("underline")}
                      className="format-btn"
                      title="Underline"
                    >
                      <FiUnderline size={12} />
                    </button>

                    <div className="toolbar-divider"></div>

                    <button
                      onClick={() => applyFormat("justifyLeft")}
                      className="format-btn"
                      title="Align Left"
                    >
                      <FiAlignLeft size={12} />
                    </button>
                    <button
                      onClick={() => applyFormat("justifyCenter")}
                      className="format-btn"
                      title="Align Center"
                    >
                      <FiAlignCenter size={12} />
                    </button>
                    <button
                      onClick={() => applyFormat("justifyRight")}
                      className="format-btn"
                      title="Align Right"
                    >
                      <FiAlignRight size={12} />
                    </button>

                    <div className="toolbar-divider"></div>

                    <button
                      onClick={() => applyFormat("insertUnorderedList")}
                      className="format-btn"
                      title="Bullet List"
                    >
                      <FiList size={12} />
                    </button>
                    <button
                      onClick={() => applyFormat("insertOrderedList")}
                      className="format-btn"
                      title="Numbered List"
                    >
                      <MdFormatListNumbered size={12} />
                    </button>

                    <div className="toolbar-divider"></div>

                    <div className="color-picker-wrapper">
                      <MdFormatColorText size={12} className="color-icon" />
                      <input
                        type="color"
                        onChange={(e) =>
                          applyFormat("foreColor", e.target.value)
                        }
                        className="color-picker"
                        title="Text Color"
                      />
                    </div>

                    <div className="toolbar-divider"></div>
                  </div>

                  <div className="toolbar-right-vertical">
                    <button
                      onClick={exportToSurveyBuilder}
                      className="btn-success stacked-btn"
                      disabled={isExporting || !output.trim()}
                      style={{
                        backgroundColor: "#10b981",
                        opacity: isExporting || !output.trim() ? 0.5 : 1,
                      }}
                    >
                      {isExporting ? (
                        <>
                          <FiLoader size={12} className="spinner" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <FiUpload size={12} />
                          To Survey
                        </>
                      )}
                    </button>

                    <button
                      onClick={startNewQuestionnaire}
                      className="btn-secondary stacked-btn"
                    >
                      <FiPlus size={12} />
                      New Qnr
                    </button>

                    <button
                      onClick={exportToWord}
                      className="btn-primary stacked-btn"
                    >
                      <FiDownload size={12} />
                      Export
                    </button>
                  </div>
                </div>

                {/* Editable Content Area */}
                <div
                  ref={outputRef}
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="content-area"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
